import {
  Dictionary,
  DrupalDocumentMedia,
  DrupalSearchApiResponseMeta,
  DrupalSearchApiTerm,
  FacetFilters
} from "@/types/hygiena-types";
import {DrupalSearchApiFacet, DrupalTaxonomyTerm} from "next-drupal";
import {Link} from "@/components/atoms/link";
import {
  createRef, HTMLProps,
  MouseEvent, ReactElement,
  useContext,
  useEffect,
  useRef,
  useState
} from "react";
import classNames from "classnames";
import {useRouter} from "next/router";
import {LoadingSpinnerOverlay} from "@/components/atoms/loading-spinner";
import {Button} from "../atoms/button";
import {documentSearch} from "@/lib/document-search";
import {getDocumentUri} from "@/lib/get-document-uri";
import {DictionaryContext} from "@/context/dictionary-context";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue
} from "../shadcn/ui/select";

interface Props {
  documents?: DrupalDocumentMedia[],
  meta?: DrupalSearchApiResponseMeta,
  hideFacets?: string[],
  preFilter?: {path:string, value: string, operator: string}[]
}

/**
 * Renders a facet select.
 *
 * @param facet
 *   The facet for this select.
 * @param activeFilters
 *   The active filters for all facets.
 * @param setFilters
 *   The set state handler for the facets.
 * @param preFilter
 *   Pre-filter by a specific set of filters that will be applied everytime to confine search.
 * @param type
 *   Currently defaults to select and also allows text.
 * @param inputClassName
 *   The input field classname.
 * @param placeholder
 *   The input field placeholder text.
 * @param selectText
 *   The text to use for the selector.
 * @param facetRef
 *   A ref to the facet.
 * @param className
 *   Extra class names.
 * @param loading
 *   Optionally pass the loading state.
 * @param autocompleteFacet
 *   An optional facet object to use as an autocomplete if the facet is a textual search.
 * @param compareFacet
 *   An optional facet object that matches the current facet to compare against, items not existing in the compare facet
 *   will be marked as disabled.
 * @constructor
 */
export function DocumentSearchFacet({facet, activeFilters, setFilters, preFilter, type, inputClassName, placeholder, selectText, facetRef, className, autocompleteFacet, loading, compareFacet}: {
  facet: DrupalSearchApiFacet,
  type?: string,
  activeFilters: FacetFilters,
  setFilters: Function,
  preFilter?: {path:string, value: string, operator: string}[]
  inputClassName?: string,
  placeholder?: string,
  selectText?: string,
  facetRef?: any
  className?: string,
  loading?: boolean,
  autocompleteFacet?: DrupalSearchApiFacet|null,
  compareFacet?: DrupalSearchApiFacet|null,
}): JSX.Element {
  const t = useContext<Dictionary>(DictionaryContext)
  const ready = useRef(false);
  const router = useRouter();
  const selected = getSelected(facet, activeFilters);
  const [searchText, setSearchText] = useState<string|null>(null);
  const [autocompleteResult, setAutoCompleteResult] = useState<DrupalSearchApiTerm[]>();
  const [autocompleteDisplay, setAutocompleteDisplay] = useState<boolean>(false);
  const [initFullText, setInitFullTest] = useState<boolean>(false);
  const hideTerms: string[] = [];
  preFilter = Array.isArray(preFilter) ? preFilter : [];

  for (const item of preFilter) {
    if (item.path === facet.path) {
      if (["NOT IN", "<>"].includes(item.operator)) {
        hideTerms.push(item.value);
      }
    }
  }

  /**
   * Compares a facet term to the compareFacet array, returns true if the term doesn't exist in the compare facet array.
   * @param term
   */
  function compareFacetTerm(term: DrupalSearchApiTerm): boolean {
    const activeCount = Object.keys(activeFilters).length;
    if (activeCount < 1) {
      return false;
    }
    if (activeCount === 1 && activeFilters?.[compareFacet?.path ?? ""]) {
      return false
    }
    if (compareFacet) {
      if (compareFacet?.terms?.length) {
        for (const compareTerm of compareFacet.terms) {
          if (term?.values?.value === compareTerm?.values?.value) {
            return false;
          }
        }
      }
      return true;
    }
    return false;
  }

  /**
   * Get the currently selected value.
   * @param facet
   * @param activeFilters
   */
  function getSelected(facet?: DrupalSearchApiFacet, activeFilters?: FacetFilters): string | number | readonly string[] | undefined {
    const selected = (facet?.path && activeFilters?.[facet.path]) ? activeFilters[facet.path] : "";
    return selected ?? undefined;
  }

  // Debounce the search text if we're using it.
  useEffect(() => {
    if (!ready?.current) {
      ready.current = true;
      return;
    }
    if (type === "text" && searchText !== null) {
      const handler = setTimeout(async () => {
        setFilters(facet.path, searchText);
        if (autocompleteFacet) {
          const Fuse = (await import('fuse.js')).default;
          const search = new Fuse(autocompleteFacet?.terms ?? [], {keys: ["values.label"], threshold: 0.4});
          const result = search.search(searchText);
          let values: DrupalSearchApiTerm[] = [];
          for (let item of result) {
            if (item?.item?.values?.label === searchText) {
              values = [];
              break;
            }
            values.push(item?.item);
          }
          setAutocompleteDisplay(values?.length > 0);
          setAutoCompleteResult(values);
        }
      }, 750);

      return () => {
        clearTimeout(handler);
      }
    }
  }, [searchText]);

  useEffect(() => {
    // Pre-populate the input text field for a full-text search, if present in the url.
    if (!initFullText && type === "text" && facet?.id && router?.query?.[facet.id] && facetRef?.current) {
      setInitFullTest(true);
      facetRef.current.value = router.query[facet.id];
    }
  }, [router?.query]);
  return (
    <label htmlFor={facet.id}
           className={classNames("font-semibold w-full", {"md:max-w-[20%]": !type || type === "select"}, className ?? "")}>
      {facet.label}
      <br/>
      {type === 'text' && !autocompleteFacet && (
        <input type="text"
               id={`facet--${facet.id}`}
               placeholder={placeholder ?? undefined}
               ref={facetRef ?? undefined}
               onChange={event => setSearchText(event.target.value)}
               className={classNames("block px-4 py-2 border-hygienaGray border rounded-sm font-normal mt-2 w-full md:max-w-[90%]", inputClassName ?? "")}/>
      )}
      {type === 'text' && autocompleteFacet && (
        <div
          onMouseLeave={(event) => {
            const element = event.relatedTarget as HTMLElement;
            if (!loading && !element?.classList?.contains("loading-spinner")) {
              setAutocompleteDisplay(false);
            }
          }}
          className={classNames("relative inline-block", inputClassName ?? "")}>
          <input type="text"
                 onBlur={() => {
                   if (typeof window !== undefined) {
                     setTimeout(() => {
                       setAutocompleteDisplay(false);
                     }, 100);
                   }
                 }}
                 onFocus={() => setAutocompleteDisplay(true)}
                 id={`facet--${facet.id}`}
                 placeholder={placeholder ?? undefined}
                 ref={facetRef ?? undefined}
                 onChange={event => setSearchText(event.target.value)}
                 className={classNames("autocomplete-facet block px-4 py-2 border-hygienaGray border rounded-sm font-normal mt-2 w-full md:max-w-[90%]", inputClassName ?? "")}/>
          <div
            className={classNames("w-full bg-white absolute z-10 py-2 transition-all drop-shadow rounded-b-md max-md:hidden overflow-auto", {
            "h-0 !p-0": !autocompleteDisplay || !autocompleteResult?.length,
          })}>
            {autocompleteResult?.length ? (
              <>
                {autocompleteResult.map(item => (
                  <button key={item.url}
                       onClick={() => {
                         if (facetRef?.current) facetRef.current.value = item.values.label;
                         setSearchText(item.values.label);
                         setAutocompleteDisplay(false);
                         setAutoCompleteResult([]);
                       }}
                       className="py-1.5 px-4 cursor-pointer hover:text-primary block z-10">
                    {item?.values?.label}
                  </button>
                ))}
              </>
            ): <></>}
          </div>
        </div>
      )}
      {(!type || type === 'select') && (
        <Select
                name={facet.id}
                value={selected ?? undefined}
                placeholder={selectText ?? `${t?.documents?.select}`}
                onValueChange={val => setFilters(facet.path, val)}>
          <SelectTrigger className={classNames("border-hygienaGray border rounded-sm text-left font-normal mt-2 w-full bg-white", inputClassName ?? "")} >
            <SelectValue placeholder={selectText ?? `${t?.documents?.select}`} />
          </SelectTrigger>
          <SelectContent>
            {facet?.terms && facet.terms.map(term => {
              if (!hideTerms.includes(term.values.value)) {
                if (!term?.["children"]?.[0]?.length) {
                  return <SelectItem key={term.values.value} value={term.values.value} className="pl-6" disabled={compareFacetTerm(term)}>{term.values.label}</SelectItem>
                }
                else {
                  return (
                    <>
                      <SelectGroup key={`industry-term--${term.values.value}`}>
                        <SelectItem key={term.values.value} value={term.values.value} className="pl-6" disabled={compareFacetTerm(term)}>{term.values.label}</SelectItem>
                        {term["children"][0].map(term => (
                          <SelectItem key={term.values.value} value={term.values.value} className="pl-10" disabled={compareFacetTerm(term)}>{term.values.label}</SelectItem>
                        ))}
                      </SelectGroup>
                    </>
                  )
                }
              }
            })}
          </SelectContent>
        </Select>
        // <select id={`document-search--${facet.id}`} name={facet.id}
        //         onChange={event => setFilters(facet.path, event.target.value)}
        //         value={selected}
        //         className={classNames("block px-4 py-2 border-hygienaGray border rounded-sm font-normal mt-2 w-full", inputClassName ?? "")}>
        //   <option value={0}>{selectText ?? `${t?.documents?.select}`}</option>
        //   {facet?.terms && facet.terms.map(term => {
        //     if (!hideTerms.includes(term.values.value)) {
        //       return <option key={term.values.value} value={term.values.value} className="bg-white" disabled={compareFacetTerm(term)}>{term.values.label}</option>
        //     }
        //   })}
        // </select>
      )}
    </label>
  )
}

/**
 * Builds the document search pagination.
 *
 * @todo make this a reusable component placed elsewhere to reuse across the site.
 *
 * @constructor
 */
export function DocumentPagination({count, page, limit, changePage, radius}): JSX.Element {
  const t = useContext<Dictionary>(DictionaryContext)
  const pages = Math.ceil(count / limit);
  const firstPage = page <= 0;
  const lastPage = page + 1 >= pages;
  let pageRange: number[] = [];
  let radiusOffsetStart = 0;
  let radiusOffsetEnd = 0;
  let diff = 0;
  pageRange = Array.from(Array(pages).keys());

  // The radius is to always keep the desired number of items showing i.e. 6 will
  // show something like this ... 8 9 10 11 12 13 14 15 16 ...
  if (pages > radius) {
    // Calculate the start offset.
    radiusOffsetStart = page - (radius / 2);
    radiusOffsetStart = radiusOffsetStart > 0 ? radiusOffsetStart : 0;

    // Calculate the end offset.
    radiusOffsetEnd = (radius / 2) + page + 1;
    radiusOffsetEnd = radiusOffsetEnd > pages ? pages : radiusOffsetEnd
    radiusOffsetEnd = radiusOffsetEnd < radius ? radius: radiusOffsetEnd;

    // Check for a radius difference to see if we need to add some back padding.
    diff = radius - (radiusOffsetEnd - radiusOffsetStart);
    if (diff > 1) {
      radiusOffsetStart = radiusOffsetStart - diff;
    }

    // Calculate the page range.
    pageRange = pageRange.slice(Math.floor(radiusOffsetStart), Math.ceil(radiusOffsetEnd));
  }


  return (
    <div className="flex gap-4 justify-end">
      {pages > 1 ? (
        <>
          {!firstPage && (
            <>
              <button onClick={() => changePage(0)} className="hidden md:inline">{`${t?.documents?.first}`}</button>
              <button onClick={() => changePage(page - 1)} className="hidden md:inline">{`${t?.documents?.prev}`}</button>
            </>
          )}
          {pageRange && pageRange[0] > 0 && <span>...</span>}
          {pageRange.map(i => (
            <button key={i} onClick={() => changePage(i)} className={classNames({"text-primary font-bold": i === page})}>
              {i + 1}
            </button>
          ))}
          {pageRange && pageRange.slice(-1)[0] < pages - 1 && <span>...</span>}
          {!lastPage && (
            <>
              <button onClick={() => changePage(page + 1)} className="hidden md:inline">{`${t?.documents?.next}`}</button>
              <button onClick={() => changePage(pages - 1)} className="hidden md:inline">{`${t?.documents?.last}`}</button>
            </>
          )}
        </>
      ) : <></>}
    </div>
  )
}

/**
 * Provides a search form and functionality for searching documentation.
 *
 * @constructor
 */
export function WidgetDocumentSearch({documents, meta, hideFacets = [], preFilter}: Props): JSX.Element {
  const t = useContext<Dictionary>(DictionaryContext)
  const { locale, defaultLocale } = useRouter();
  const [documentCount, setDocumentCount] = useState(meta?.count ?? 0);
  const [pageLimit, setPageLimit] = useState(10);
  const [page, setPage] = useState(0);
  const [activeFilters, setActiveFilters] = useState({} as FacetFilters);
  const [activeDocuments, setActiveDocuments] = useState(documents);
  const [activeMeta, setActiveMeta] = useState(meta);
  const [loading, setLoading] = useState<boolean>();
  const widgetRef = createRef<HTMLDivElement>();
  const searchTextRef = createRef<HTMLInputElement>();
  const docLinkProps: HTMLProps<HTMLLinkElement> = locale !== defaultLocale
    ? { rel: 'noindex nofollow'}
    : {};

  // Create a dummy facet for the document search aggregated field (which is not a facet).
  const titleFacet = {id: "document_search", path: "document_search", terms: [], label: `${t?.documents?.search}`};

  async function tagClick(event: MouseEvent<HTMLSpanElement>) {
    const tag = event.target as HTMLSpanElement;
    const tid = tag.getAttribute("data-tid");
    const facetMachineName = tag.getAttribute("data-facet");
    scrollToRef();
    if (facetMachineName && tid) {
      await updateFilters(facetMachineName, tid);
    }
  }

  /**
   * Creates selectable terms to filter on.
   *
   * @param machineName
   *
   * @param terms
   *   The terms object array or term object.
   */
  function getDocumentTerms(machineName: string, terms: DrupalTaxonomyTerm|DrupalTaxonomyTerm[], limit?: number): JSX.Element[] {
    const items: ReactElement[] = [];
    if (Array.isArray(terms)) {
      if (limit) {
        terms = terms.slice(0, limit);
      }
      terms.map((term, index) => items.push(
        <span data-facet={machineName}
              key={term.id}
              data-tid={term.drupal_internal__tid}
              onClick={(event) => {tagClick(event)}}
              className="cursor-pointer">
          {term.name}{index !== terms.length - 1 && (<>,&nbsp;</>)}
        </span>
      ));
    }
    else if (terms?.name) {
      items.push(
        <span data-facet={machineName}
              key={terms.id}
              data-tid={terms.drupal_internal__tid}
              onClick={(event) => {tagClick(event)}}
              className="cursor-pointer">{terms.name}</span>
      );
    }
    return items;
  }

  async function searchDocuments(activeFilters: FacetFilters, pageLimit: number, page: number) {
    const pageOffset = Math.ceil(pageLimit * page);
    const result = await documentSearch(activeFilters, pageLimit, pageOffset, preFilter ?? [], locale, defaultLocale);
    setActiveDocuments(result?.data?.documents ?? []);
    setActiveMeta(result?.data?.meta ?? {});
    setDocumentCount(result?.data?.meta?.count ?? 0);
    setLoading(false);
  }

  /**
   * Updates the active filters and performs a new faceted search.
   *
   * @param key
   * @param val
   */
  async function updateFilters(key: string, val: string) {
    if (!val || val === "0") {
      delete activeFilters[key];
    }
    else {
      activeFilters[key] = val;
    }
    await setLoading(true);
    await setActiveFilters(activeFilters);
    await setPage(0);
    await searchDocuments(activeFilters, pageLimit, 0);
  }

  /**
   * Resets the filters and re-fetches.
   */
  async function resetFilters() {
    if (searchTextRef?.current) {
      searchTextRef.current.value = '';
    }
    await setLoading(true);
    await setActiveFilters({});
    await setPage(0);
    await searchDocuments({}, pageLimit, 0);
  }

  /**
   * Updates the allowed number of pages and refreshes the documents.
   *
   * @param pageLimit
   */
  async function updatePageLimit(pageLimit: number) {
    await setLoading(true);
    scrollToRef();
    await setPageLimit(pageLimit);
    await setPage(0);
    await searchDocuments(activeFilters, pageLimit, 0);
  }

  /**
   * Handler for pagination events.
   */
  async function changePage(page) {
    await setLoading(true);
    scrollToRef();
    await setPage(page);
    await searchDocuments(activeFilters, pageLimit, page);
  }

  /**
   * Scrolls back to the top of the document search.
   *
   * @todo this is used in a few places, refactor it into a reusable function.
   */
  function scrollToRef() {
    if (!widgetRef?.current) return;
    const current = widgetRef.current;
    current.scrollIntoView({behavior: 'smooth'})
  }
  return (
    <div className="relative">
      {/* Add a gray overlay and spinner when loading */}
      <LoadingSpinnerOverlay loading={loading} fixed={true}/>
      <div className="inline-block w-full scroll-m-20" ref={widgetRef}>
        <div className="bg-white flex flex-col md:flex-row p-6 gap-6 text-black items-center">
          {titleFacet && (
            <DocumentSearchFacet inputClassName="lg:min-w-[400px] lg:mr-6 lg:max-w-[500px]" placeholder={`${t?.documents?.search_documents}`} key={titleFacet.id} facet={titleFacet} activeFilters={activeFilters} setFilters={updateFilters} preFilter={preFilter} type="text" facetRef={searchTextRef}/>
          )}
          {meta?.facets && meta.facets.map(facet => {
            let selectText = `${t?.documents?.select}`;

            if (!hideFacets.includes(facet?.path ?? "") && facet?.terms && facet?.terms.length > 1) {
              const compareFacet = activeMeta?.facets?.filter(item => item?.id === facet?.id)?.[0];
              return <DocumentSearchFacet key={facet.id} facet={facet} activeFilters={activeFilters} setFilters={updateFilters} preFilter={preFilter} selectText={selectText} compareFacet={compareFacet} />
            }
          })}
          {Object.keys(activeFilters).length ? (
            <button onClick={resetFilters} className={"bg-accent-red transition-all border-2 border-accent-red text-hygienaLight px-6 py-2 self-end hover:bg-white hover:text-accent-red hover:border-accent-red"}>{`${t?.documents?.reset}`}</button>
          ) : <></>}
        </div>
        <form className="w-full pb-6 pt-4" onSubmit={(event) => event.preventDefault()}>
          <div className="overflow-auto">
            <table className="w-full table-auto text-black">
              <thead className="text-left text-black bg-white">
                <tr className="border-b-hygienaLight border-b-4">
                  <th className="py-3 px-2 pl-6 lg:min-w-[400px] lg:text-xl font-semibold">{`${t?.documents?.title}`}</th>
                  {!hideFacets.includes("field_document_categories") && (
                    <th className="py-3 px-2 lg:text-xl font-semibold">{`${t?.documents?.categories}`}</th>
                  )}
                  {!hideFacets.includes("field_document_language") && (
                    <th className="py-3 px-2 lg:text-xl font-semibold">{`${t?.documents?.language}`}</th>
                  )}
                  {!hideFacets.includes("field_document_industry") && (
                    <th className="py-3 px-2 hidden md:table-cell lg:text-xl font-semibold">{`${t?.documents?.industry}`}</th>
                  )}
                  {!hideFacets.includes("field_document_region") && (
                    <th className="py-3 px-2 lg:text-xl font-semibold">{`${t?.documents?.region}`}</th>
                  )}
                  <th/>
                </tr>
                </thead>
              <tbody>
              {activeDocuments?.map(item => (
                <tr key={item.id} className="even:bg-hygienaLight odd:bg-white">
                  <td className="px-2 py-3 lg:min-w-[400px] pl-6">
                    <div className="flex gap-4 items-center">
                      {item?.field_media_file?.uri?.url && (
                        <Button href={getDocumentUri(item)} hover={{color: "primary"}} icon={"file-search"} height={25} width={25} iconClass="!py-0 !mr-0" className="!p-1.5" target={"_blank"} {...docLinkProps}/>
                      )}
                      <Link href={getDocumentUri(item) ?? "/"} className="hover:text-primary transition-all duration-300" target="_blank" {...docLinkProps}>{item.name}</Link>
                    </div>
                  </td>
                  {!hideFacets.includes("field_document_categories") && (
                    <td className="text-primary px-2 py-3">
                      {item?.field_document_categories && getDocumentTerms("field_document_categories", item.field_document_categories)}
                    </td>
                  )}
                  {!hideFacets.includes("field_document_language") && (
                    <td className="text-primary px-2 py-3 break-all">
                      {item?.field_document_language && getDocumentTerms("field_document_language", item.field_document_language)}
                    </td>
                  )}
                  {!hideFacets.includes("field_document_industry") && (
                    <td className="px-2 py-3 hidden md:table-cell">
                      {item?.field_document_industry && getDocumentTerms("field_document_industry", item.field_document_industry)}
                    </td>
                  )}
                  <td className="pr-6"></td>
                </tr>
              ))}
              </tbody>
            </table>
            <div className="flex flex-col md:flex-row gap-6 justify-left items-center px-6 bg-white py-6 justify-between">
              <label htmlFor="document-search--per-page" className="hidden md:inline">
                {`${t?.documents?.show}`}
                <select id="document-search--per-page" name="per_page"
                        onChange={(event) => updatePageLimit(parseInt(event.target.value))}
                        value={pageLimit}
                        className="px-2 py-1 border border-black rounded-sm mx-2">
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                </select>
                {`${t?.documents?.per_page}`}
              </label>
              <div className="max-md:self-baseline lowercase">{documentCount} {documentCount > 1 || documentCount === 0 ? `${t?.documents?.documents}` : `${t?.documents?.document}`}</div>
              <DocumentPagination count={documentCount} page={page} limit={pageLimit} changePage={changePage} radius={8}/>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}